import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface NotificationFormProps {
  onNotificationSent: () => void;
  selectedClientId?: number | null;
  selectedClientName?: string | null;
}

export const NotificationForm = ({ 
  onNotificationSent, 
  selectedClientId,
  selectedClientName 
}: NotificationFormProps) => {
  const [newMessage, setNewMessage] = useState('');
  const { toast } = useToast();
  const { user, isAdmin } = useAuth();

  const sendMessage = async () => {
    if (!newMessage.trim() || !user) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a message"
      });
      return;
    }

    if (isAdmin && !selectedClientId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select a client to send the message to"
      });
      return;
    }

    try {
      const notificationData = {
        message: newMessage,
        is_from_admin: isAdmin,
        admin_id: isAdmin ? user.id : null,
        user_id: !isAdmin ? user.id : null,
        client_id: selectedClientId,
      };

      const { error } = await supabase
        .from('notifications')
        .insert([notificationData]);

      if (error) throw error;

      setNewMessage('');
      onNotificationSent();
      toast({
        title: "Success",
        description: "Message sent successfully"
      });
    } catch (error: any) {
      console.error('Error in sendMessage:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to send message"
      });
    }
  };

  return (
    <div className="flex flex-col space-y-4 max-w-2xl">
      {selectedClientName && (
        <div className="text-sm text-gray-400">
          Sending message to: {selectedClientName}
        </div>
      )}
      <div className="flex gap-4">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={selectedClientId ? "Type your message..." : "Select a client first..."}
          className="flex-1 px-4 py-2 rounded-lg bg-[#2A2F3C] text-white border border-gray-600 focus:outline-none focus:border-blue-500"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              sendMessage();
            }
          }}
          disabled={!selectedClientId}
        />
        <Button 
          onClick={sendMessage} 
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
          disabled={!selectedClientId}
        >
          <Send className="w-4 h-4" />
          Send
        </Button>
      </div>
    </div>
  );
};